/**
 * Camera Permission Management Utilities
 * Handles persistent camera permissions across sessions
 */

const PERMISSION_KEY = 'camera-permission-granted';
const PERMISSION_TIMESTAMP_KEY = 'camera-permission-timestamp';
const PERMISSION_PROMPT_COUNT_KEY = 'camera-permission-prompt-count';

/**
 * Check if we have stored camera permission
 */
export const hasStoredPermission = () => {
  const stored = localStorage.getItem(PERMISSION_KEY);
  const timestamp = localStorage.getItem(PERMISSION_TIMESTAMP_KEY);
  
  if (!stored || !timestamp) return false;
  
  // Check if permission is older than 7 days (iOS often resets)
  const daysSinceGrant = (Date.now() - parseInt(timestamp)) / (1000 * 60 * 60 * 24);
  if (daysSinceGrant > 7) {
    clearStoredPermission();
    return false;
  }
  
  return stored === 'true';
};

/**
 * Store camera permission grant
 */
export const storePermission = () => {
  localStorage.setItem(PERMISSION_KEY, 'true');
  localStorage.setItem(PERMISSION_TIMESTAMP_KEY, Date.now().toString());
  
  // Track how many times permission was granted
  const promptCount = parseInt(localStorage.getItem(PERMISSION_PROMPT_COUNT_KEY) || '0');
  localStorage.setItem(PERMISSION_PROMPT_COUNT_KEY, (promptCount + 1).toString());
};

/**
 * Clear stored permission
 */
export const clearStoredPermission = () => {
  localStorage.removeItem(PERMISSION_KEY);
  localStorage.removeItem(PERMISSION_TIMESTAMP_KEY);
};

/**
 * Check browser permission API if available
 */
export const checkBrowserPermission = async () => {
  try {
    if (!navigator.permissions) {
      return 'unsupported';
    }
    
    const result = await navigator.permissions.query({ name: 'camera' });
    return result.state; // 'granted', 'denied', or 'prompt'
  } catch (err) {
    return 'unsupported';
  }
};

/**
 * Request camera with optimal settings for persistence
 */
export const requestCameraAccess = async () => {
  try {
    const constraints = {
      video: {
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };
    
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    
    // Permission was granted, store it
    storePermission();
    
    return { success: true, stream };
  } catch (err) {
    console.error('Camera access denied:', err);
    
    if (err.name === 'NotAllowedError') {
      return { 
        success: false, 
        error: 'Camera access denied. Please check your browser settings.',
        isDenied: true 
      };
    } else if (err.name === 'NotFoundError') {
      return { 
        success: false, 
        error: 'No camera found on this device.',
        isNotFound: true 
      };
    } else {
      return { 
        success: false, 
        error: 'Unable to access camera. Please try again.',
        isOtherError: true 
      };
    }
  }
};

/**
 * Get user-friendly instructions based on browser
 */
export const getPermissionInstructions = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return {
      platform: 'iOS',
      instructions: [
        'When prompted, tap "Allow" to grant camera access',
        'iOS may ask again after app updates or restarts',
        'For permanent access, add this site to your Home Screen'
      ]
    };
  } else if (/android/.test(userAgent)) {
    return {
      platform: 'Android',
      instructions: [
        'Tap "Allow" when prompted for camera access',
        'Choose "While using the app" for best experience',
        'Permission should persist between visits'
      ]
    };
  } else {
    return {
      platform: 'Desktop',
      instructions: [
        'Click "Allow" when prompted for camera access',
        'Permission will be remembered for this site',
        'Check the camera icon in your address bar'
      ]
    };
  }
};

/**
 * Check if we should show permission tips
 */
export const shouldShowPermissionTips = () => {
  const promptCount = parseInt(localStorage.getItem(PERMISSION_PROMPT_COUNT_KEY) || '0');
  return promptCount === 0; // Only show tips on first use
};